import React, { useCallback, useMemo, useState } from 'react'
import { Formik, Form } from 'formik'
import {
  LoaderButton
} from 'components/molecules'
import PropTypes from 'prop-types'
import TlBankSelectSearchField from 'domains/tl-banks/molecules/select-search-field/tl-bank-select-search-field'
import * as Yup from 'yup'
import { useDropzone } from 'react-dropzone'
import { Alert, Button } from 'reactstrap'

const propTypes = {
  onClickSubmitForm: PropTypes.func.isRequired
}

const TEMPLATE_LINK = 'https://drive.google.com/drive/folders/1qLdVMbiiikWX58-9Pm2ILMAaboS2lHxX?usp=drive_link'

const validationSchema = Yup.object().shape({
  file    : Yup.mixed().required('File is required'),
  tl_bank : Yup.object().shape({
    id: Yup.number().required('Required')
  })
})

/**
 * Form to upload bankStatement Csv
 * @param {Function} onClickSubmitForm - function to submit form
 */

const BankTransactionUploadCsvForm = ({
  onClickSubmitForm
}) => (
  <Formik
    initialValues={{
      file    : '',
      tl_bank : ''
    }}
    enableReinitialize={ true }
    onSubmit={ onClickSubmitForm }
    validationSchema={ validationSchema }
  >
    {
      ( formikProps ) => (
        <Form>
          <TlBankSelectSearchField
            name={ 'tl_bank' }
            label={ 'Bank' }
            required={ true }
            placeholder='Search with Bank name or account number'
            showDescription={ true }
            formText='Tinkerlust Bank yang digunakan finance'
          />
          <UploadCSVField formikProps={ formikProps } name='file'/>
          <br/>
          <div>
            <LoaderButton
              color='success'
              isLoading={ formikProps.isSubmitting }
              type='submit'
              className='float-right'
              text={ 'Upload' }
            />
            <br/>
            <br/>
            <div className='small text-muted float-right'>Upload File dan Bulk create Finance::BankTransaction</div>
          </div>
        </Form>
      )
    }
  </Formik>
)

const UploadCSVField = ({ formikProps, name }) => {
  const [ file, setFile ] = useState(new Blob)

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
      formikProps.setFieldValue(name, acceptedFiles[0])
    } else {
      alert('Please upload file with .csv format.')
    }

  }, [ ])

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    accept: 'text/csv, application/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })

  /**
    * Upload Form Styling Begins
    */
  const baseStyle = useMemo(() => ({
    flex            : 1,
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'center',
    margin          : '20px',
    padding         : '20px',
    borderWidth     : 2,
    borderRadius    : 2,
    borderColor     : '#eeeeee',
    borderStyle     : 'dashed',
    backgroundColor : '#fafafa',
    color           : '#bdbdbd',
    outline         : 'none',
    transition      : 'border .24s ease-in-out'
  }), [])

  const activeStyle = useMemo(() => ({
    borderColor: '#2196f3'
  }), [])

  const acceptStyle = useMemo(() => ({
    borderColor: '#00e676'
  }), [])

  const rejectStyle = useMemo(() => ({
    borderColor: '#ff1744'
  }), [])

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {})
  }), [
    isDragActive,
    isDragReject,
    isDragAccept,
    baseStyle,
    activeStyle,
    acceptStyle,
    rejectStyle
  ])

  const showUploadedFile = file.size === 0
    ?  'Drag and drop a CSV file here, or click to select files to upload bank statement CSV'
    : file.name

  return (
    <>
      <Alert
        color="info"
        className='m-3'
      >
        <i className="fa fa-info-circle mr-1 text-dark" aria-hidden="true" />
        <span className='uppercase h4'>
          { 'PERHATIAN' }
        </span>
        <div>
          <h4>
            { 'Silahkan upload bank statement CSV seperti contoh, ' }
            <a
              className='text-primary'
              href={ TEMPLATE_LINK }
              target='_blank' rel='noopener noreferrer'
            >
              { 'disini' }
            </a>
          </h4>
        </div>
      </Alert>
      <div { ...getRootProps({ style }) }>
        <input { ...getInputProps() } />
        <p className='font-weight-bold'>
          { showUploadedFile }
        </p>
      </div>
      {
        formikProps.errors[name] && (
          <small className='mt-3 text-danger'>
            { formikProps.errors[name] }
          </small>
        ) }

      {
        formikProps.values.file !== '' && (
          <>
            <Button
              color='primary'
              className='m-3'
              onClick={ () => {
                setFile(new Blob())
                formikProps.setFieldValue(name, '')
              } }
            >
              { 'Clear File' }
            </Button>
            <div className='small text-muted'>
              Reset File Input, hapus file yang sudah di pilih
            </div>
          </>
        )
      }
    </>
  )
}
UploadCSVField.propTypes = { formikProps: PropTypes.any, name: PropTypes.string }

BankTransactionUploadCsvForm.propTypes = propTypes

export default BankTransactionUploadCsvForm
